'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeroSectionProps {
  onGetQuote: () => void;
  onViewProducts: () => void;
}

const HeroSection = ({ onGetQuote, onViewProducts }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-primary" aria-label="Hero section">
      <div className="container mx-auto px-5 md:px-10 py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Title and Content */}
          <article className="text-left">
            {/* Keyword Badge */}
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-accent/30 rounded-full mb-6 animate-fadeIn border-2 border-accent/50">
              <Icon name="SparklesIcon" size={24} variant="solid" className="text-accent" aria-hidden="true" />
              <span className="font-headline font-bold text-lg text-white">On-site Installation</span>
            </div>

            <h1 className="font-headline font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight animate-fadeIn">
              Premium Seamless LED Video Walls Direct from Factory
            </h1>

            <p className="font-headline font-semibold text-xl md:text-2xl text-accent mb-4 animate-fadeIn">
              Save 40-60% with Custom LED Wall Manufacturing
            </p>

            <p className="font-body text-lg md:text-xl text-white/90 mb-8 animate-fadeIn">
              Factory-direct seamless LED video walls and indoor LED screens for retail, corporate, and commercial spaces. No gaps between panels. ISO certified quality with international support.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4 mb-12 animate-fadeIn">
              <button
                onClick={onGetQuote}
                className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-accent text-accent-foreground rounded-lg font-headline font-semibold text-lg shadow-cta hover:bg-accent/90 transition-all duration-250"
                aria-label="Get instant quote for LED video wall">

                <Icon name="DocumentTextIcon" size={24} variant="outline" aria-hidden="true" />
                <span>Get Instant Quote</span>
              </button>
              <button
                onClick={onViewProducts}
                className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-white/10 text-white border-2 border-white/40 rounded-lg font-headline font-semibold text-lg hover:bg-white/20 transition-all duration-250"
                aria-label="View our LED video wall products">
                <Icon name="Squares2X2Icon" size={24} variant="outline" aria-hidden="true" />
                <span>View Products</span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-fadeIn" role="list" aria-label="Company statistics">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" role="listitem">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-accent" aria-hidden="true" />
                  <span className="font-accent font-bold text-2xl text-white">5000+</span>
                </div>
                <p className="font-body text-sm text-white/80">LED Wall Projects</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" role="listitem">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="StarIcon" size={20} variant="solid" className="text-accent" aria-hidden="true" />
                  <span className="font-accent font-bold text-2xl text-white">98%</span>
                </div>
                <p className="font-body text-sm text-white/80">Satisfaction Rate</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" role="listitem">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="BoltIcon" size={20} variant="solid" className="text-accent" aria-hidden="true" />
                  <span className="font-accent font-bold text-2xl text-white">7-15</span>
                </div>
                <p className="font-body text-sm text-white/80">Days Production</p>
              </div>
            </div>
          </article>

          {/* Right Column - LED Video Wall Picture */}
          <figure className="relative animate-fadeIn">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-accent/30">
              <AppImage
                src="/assets/images/seamless_led_video_wall_hero.png"
                alt="Premium seamless LED video wall displays in modern shopping mall with ultra-fine pixel pitch technology and no gaps between panels, continuous smooth surface showing vibrant content"
                className="w-full h-auto object-cover"
                priority />
              
              {/* Overlay gradient for depth */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" aria-hidden="true" />
            </div>
            
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-accent/20 rounded-full blur-3xl" aria-hidden="true" />
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-accent/20 rounded-full blur-3xl" aria-hidden="true" />
          </figure>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10" aria-hidden="true">
        <Icon name="ChevronDownIcon" size={32} variant="outline" className="text-white/60" />
      </div>
    </section>);

};

export default HeroSection;